import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { MapPin, Loader2, RefreshCw, Clock, User, Wifi, WifiOff, Search, X, Navigation } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { api } from '../../services/api';
import { wsService } from '../../services/websocket';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom guard marker icon
const guardIcon = new L.DivIcon({
  className: 'custom-guard-marker',
  html: `<div style="background: #22c55e; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Selected guard marker icon
const selectedGuardIcon = new L.DivIcon({
  className: 'custom-guard-marker',
  html: `<div style="background: #3b82f6; width: 40px; height: 40px; border-radius: 50%; border: 4px solid white; box-shadow: 0 4px 12px rgba(59,130,246,0.5); display: flex; align-items: center; justify-content: center; animation: pulse 2s infinite;">
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// Component to update map view and open popup
function MapUpdater({ center, zoom, selectedGuard, markerRefs }) {
  const map = useMap();
  useEffect(() => {
    if (selectedGuard?.latitude && selectedGuard?.longitude) {
      map.setView([selectedGuard.latitude, selectedGuard.longitude], 16, { animate: true, duration: 0.8 });
      // Open the marker popup after map moves
      setTimeout(() => {
        const ref = markerRefs.current?.[selectedGuard.id];
        if (ref) ref.openPopup();
      }, 400);
    } else if (center[0] && center[1]) {
      map.setView(center, zoom);
    }
  }, [center, zoom, map, selectedGuard, markerRefs]);
  return null;
}

// Guard Card Component
const GuardCard = ({ guard, isSelected, onSelect, wsConnected }) => {
  const timeAgo = guard.updated_at 
    ? formatDistanceToNow(new Date(guard.updated_at), { addSuffix: true })
    : guard.last_update 
      ? formatDistanceToNow(new Date(guard.last_update), { addSuffix: true })
      : null;
  
  return (
    <button
      onClick={() => onSelect(guard)}
      className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
        isSelected 
          ? 'bg-blue-50 border-blue-500 shadow-md' 
          : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-sm'
      }`}
      data-testid={`guard-card-${guard.guard_id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center relative ${
          isSelected ? 'bg-blue-600' : 'bg-green-500'
        }`}>
          <User className="w-5 h-5 text-white" />
          {wsConnected && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold truncate ${isSelected ? 'text-blue-800' : 'text-slate-800'}`}>
            {guard.guard_name || guard.name}
          </p>
          {timeAgo && (
            <p className="text-xs text-green-600 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {timeAgo}
            </p>
          )}
        </div>
        {isSelected && (
          <Navigation className="w-4 h-4 text-blue-600" />
        )}
      </div>
    </button>
  );
};

export default function AdminTracking() {
  const { user, token } = useAuth();
  const [guards, setGuards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [wsConnected, setWsConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGuard, setSelectedGuard] = useState(null);
  const guardsRef = useRef(guards);
  const markerRefs = useRef({});

  // Keep ref updated
  useEffect(() => {
    guardsRef.current = guards;
  }, [guards]);

  const loadGuardLocations = useCallback(async () => {
    try {
      const data = await api.getGuardLocations();
      // Normalize field names from API (guard_id -> id, guard_name -> name)
      const normalized = (Array.isArray(data) ? data : []).map(g => ({
        ...g,
        id: g.guard_id || g.id,
        name: g.guard_name || g.name,
      }));
      setGuards(normalized);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading guard locations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle WebSocket location updates
  const handleLocationUpdate = useCallback((data) => {
    setGuards(prevGuards => {
      const existingIndex = prevGuards.findIndex(g => g.id === data.guard_id);
      if (existingIndex >= 0) {
        const updated = [...prevGuards];
        updated[existingIndex] = {
          ...updated[existingIndex],
          latitude: data.latitude,
          longitude: data.longitude,
          last_update: data.timestamp,
        };
        return updated;
      } else {
        return [...prevGuards, {
          id: data.guard_id,
          name: data.guard_name,
          latitude: data.latitude,
          longitude: data.longitude,
          site_name: data.site_name,
          last_update: data.timestamp,
        }];
      }
    });
    setLastUpdated(new Date());
  }, []);

  useEffect(() => {
    loadGuardLocations();

    if (user?.id && token) {
      wsService.connect(user.id, token);
      
      const unsubConnect = wsService.on('connected', () => setWsConnected(true));
      const unsubDisconnect = wsService.on('disconnected', () => setWsConnected(false));
      const unsubLocation = wsService.on('location_update', handleLocationUpdate);

      return () => {
        unsubConnect();
        unsubDisconnect();
        unsubLocation();
      };
    }

    const interval = setInterval(loadGuardLocations, 30000);
    return () => clearInterval(interval);
  }, [user?.id, token, loadGuardLocations, handleLocationUpdate]);

  // Filtered guards
  const filteredGuards = useMemo(() => {
    if (!searchQuery) return guards;
    return guards.filter(g => 
      g.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.site_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [guards, searchQuery]);

  // Calculate center of map based on guard locations
  const getMapCenter = () => {
    const validGuards = guards.filter(g => g.latitude && g.longitude);
    if (validGuards.length === 0) return [53.5, -1.5]; // Default to UK
    const avgLat = validGuards.reduce((sum, g) => sum + g.latitude, 0) / validGuards.length;
    const avgLng = validGuards.reduce((sum, g) => sum + g.longitude, 0) / validGuards.length;
    return [avgLat, avgLng];
  };

  const handleGuardSelect = (guard) => {
    setSelectedGuard(selectedGuard?.id === guard.id ? null : guard);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
            <p className="mt-3 text-slate-500">Loading guard locations...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-tracking-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Live Tracking</h1>
            <div className="flex items-center gap-4 mt-1">
              <p className="text-sm text-slate-500">
                Updated {format(lastUpdated, 'h:mm:ss a')}
              </p>
              <Badge variant="outline" className={wsConnected ? 'bg-green-100 text-green-700 border-green-300' : 'bg-slate-100 text-slate-500'}>
                {wsConnected ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
                {wsConnected ? 'Live' : 'Polling'}
              </Badge>
            </div>
          </div>
          <Button onClick={loadGuardLocations} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{guards.length}</p>
                  <p className="text-xs text-green-600">Active Guards</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">
                    {guards.filter(g => g.latitude && g.longitude).length}
                  </p>
                  <p className="text-xs text-blue-600">With Location</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Navigation className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">
                    {[...new Set(guards.map(g => g.site_name).filter(Boolean))].length}
                  </p>
                  <p className="text-xs text-purple-600">Active Sites</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Guards List */}
          <Card className="border-0 shadow-sm lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                Guards ({guards.length})
                {wsConnected && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1 font-normal">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                    Live
                  </span>
                )}
              </CardTitle>
              {/* Search */}
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search guards..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                  data-testid="guard-search-input"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Quick Filter Chips */}
              {guards.length <= 8 && guards.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-slate-100">
                  {guards.map((guard) => (
                    <button
                      key={guard.id}
                      onClick={() => handleGuardSelect(guard)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                        selectedGuard?.id === guard.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${
                        selectedGuard?.id === guard.id ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                      }`}>
                        {guard.name?.[0]?.toUpperCase()}
                      </div>
                      {guard.name?.split(' ')[0]}
                    </button>
                  ))}
                </div>
              )}

              {/* Guards List */}
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                {filteredGuards.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MapPin className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">
                      {searchQuery ? 'No guards match your search' : 'No guards clocked in'}
                    </p>
                  </div>
                ) : (
                  filteredGuards.map((guard) => (
                    <GuardCard
                      key={guard.id}
                      guard={guard}
                      isSelected={selectedGuard?.id === guard.id}
                      onSelect={handleGuardSelect}
                      wsConnected={wsConnected}
                    />
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Map */}
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardContent className="p-0 overflow-hidden rounded-xl">
              <div style={{ height: '520px', width: '100%' }}>
                <MapContainer
                  center={getMapCenter()}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapUpdater center={getMapCenter()} zoom={13} selectedGuard={selectedGuard} markerRefs={markerRefs} />
                  {guards.filter(g => g.latitude && g.longitude).map((guard) => (
                    <Marker 
                      key={guard.id} 
                      position={[guard.latitude, guard.longitude]}
                      icon={selectedGuard?.id === guard.id ? selectedGuardIcon : guardIcon}
                      ref={(ref) => { if (ref) markerRefs.current[guard.id] = ref; }}
                      eventHandlers={{
                        click: () => handleGuardSelect(guard)
                      }}
                    >
                      <Popup>
                        <div className="text-center min-w-[160px]">
                          <div className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center ${
                            selectedGuard?.id === guard.id ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            <User className={`w-6 h-6 ${
                              selectedGuard?.id === guard.id ? 'text-blue-600' : 'text-green-600'
                            }`} />
                          </div>
                          <p className="font-bold text-slate-800">{guard.name}</p>
                          <p className="text-sm text-blue-700">{guard.site_name || 'On Patrol'}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {guard.shift_start && `${guard.shift_start} - ${guard.shift_end}`}
                          </p>
                          {(guard.updated_at || guard.last_update) && (
                            <div className="mt-2 pt-2 border-t border-slate-100">
                              <p className="text-xs text-green-600 font-medium flex items-center justify-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDistanceToNow(new Date(guard.updated_at || guard.last_update), { addSuffix: true })}
                              </p>
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </AdminLayout>
  );
}
